/**
 * Simplified TimeHandler - NO TIMEZONE CONVERSION
 * 
 * Frontend sends and receives dates as-is without any conversion.
 * Backend handles all timezone logic.
 * 
 * Date format expectations:
 * - API requests: Send date strings as "YYYY-MM-DD" or ISO strings directly
 * - API responses: Display dates exactly as received from backend
 * - No utcToLocal, localToUtc, or any timezone manipulation
 */
const MTime = {
	/**
	 * Format a date string to YYYY-MM-DD
	 * Accepts: Date object, ISO string, or YYYY-MM-DD string
	 * Returns: YYYY-MM-DD string (no timezone conversion)
	 * 
	 * IMPORTANT: For ISO strings with time component (e.g., "2026-04-30T08:00:00.000Z"),
	 * this extracts the date portion directly to avoid timezone conversion issues.
	 */
	formatDate(dateObj) {
		if (!dateObj) return '';
		
		// If it's already a simple date string (YYYY-MM-DD), return as-is
		if (typeof dateObj === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateObj)) {
			return dateObj;
		}
		
		// If it's an ISO string with time component, extract date portion
		if (typeof dateObj === 'string' && dateObj.includes('T')) {
			// Extract YYYY-MM-DD from ISO string before any timezone conversion
			return dateObj.split('T')[0];
		}
		
		// For Date objects, extract components
		let date = typeof dateObj === 'string' ? new Date(dateObj) : dateObj;
		
		// Validate date
		if (isNaN(date.getTime())) return '';

		let month = date.getMonth() + 1;
		let day = date.getDate();

		month = month < 10 ? `0${month}` : month;
		day = day < 10 ? `0${day}` : day;

		return `${date.getFullYear()}-${month}-${day}`;
	},

	/**
	 * Format a date string to YYYY-MM-DD HH:mm:ss
	 * Accepts: Date object or ISO string
	 * Returns: Formatted datetime string (no timezone conversion)
	 * 
	 * IMPORTANT: For ISO strings, extracts components directly to avoid timezone issues.
	 */
	formatDateTime(dateObj) {
		if (!dateObj) return '';
		
		// If it's an ISO string, parse it carefully
		if (typeof dateObj === 'string') {
			// Try to extract date and time components from ISO string
			const isoMatch = dateObj.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/);
			if (isoMatch) {
				// Extract components directly from string to avoid timezone conversion
				const [, year, month, day, hours, minutes, seconds] = isoMatch;
				return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
			}
			// Fallback for other formats
			let date = new Date(dateObj);
			if (isNaN(date.getTime())) return '';
			
			let mo = date.getMonth() + 1;
			let d = date.getDate();
			let h = date.getHours();
			let m = date.getMinutes();
			let s = date.getSeconds();

			mo = mo < 10 ? `0${mo}` : mo;
			d = d < 10 ? `0${d}` : d;
			h = h < 10 ? `0${h}` : h;
			m = m < 10 ? `0${m}` : m;
			s = s < 10 ? `0${s}` : s;

			return `${date.getFullYear()}-${mo}-${d} ${h}:${m}:${s}`;
		}
		
		// For Date objects
		let date = dateObj;
		
		// Validate date
		if (isNaN(date.getTime())) return '';
		
		let month = date.getMonth() + 1;
		let day = date.getDate();
		let hours = date.getHours();
		let minutes = date.getMinutes();
		let seconds = date.getSeconds();

		month = month < 10 ? `0${month}` : month;
		day = day < 10 ? `0${day}` : day;
		hours = hours < 10 ? `0${hours}` : hours;
		minutes = minutes < 10 ? `0${minutes}` : minutes;
		seconds = seconds < 10 ? `0${seconds}` : seconds;

		return `${date.getFullYear()}-${month}-${day} ${hours}:${minutes}:${seconds}`;
	},

	/**
	 * Get current date in YYYY-MM-DD format
	 */
	getCurrentDate() {
		return this.formatDate(new Date());
	},

	/**
	 * Get current datetime in YYYY-MM-DD HH:mm:ss format
	 */
	getCurrentDateTime() {
		return this.formatDateTime(new Date());
	},

	/**
	 * Get date after N days from today
	 */
	getDateAfterDays(days) {
		let today = new Date();
		let futureDate = new Date(today);
		futureDate.setDate(today.getDate() + days);
		return this.formatDate(futureDate);
	},

	/**
	 * Get day number from date string
	 */
	getCurrentDay(dateStr) {
		if (!dateStr) return null;
		let date = new Date(dateStr);
		if (isNaN(date.getTime())) return null;
		return date.getDate();
	},

	/**
	 * Convert date to ISO string (for API submission)
	 * This is just a wrapper around toISOString() - no manual conversion
	 */
	toIsoString(dateObj) {
		if (!dateObj) return '';
		let date = typeof dateObj === 'string' ? new Date(dateObj) : dateObj;
		if (isNaN(date.getTime())) return '';
		return date.toISOString();
	},

	/**
	 * Day of the week mapping - matches .NET DayOfWeek enum
	 */
	get dayMap() {
		return {
			nameToInt: {
				"周日": 0, "周一": 1, "周二": 2, "周三": 3,
				"周四": 4, "周五": 5, "周六": 6
			},
			intToName: {
				0: "周日", 1: "周一", 2: "周二", 3: "周三",
				4: "周四", 5: "周五", 6: "周六"
			}
		};
	},

	/**
	 * Convert day name to integer
	 */
	convertDayNameToInt(dayName) {
		return this.dayMap.nameToInt[dayName] || 0;
	},

	/**
	 * Convert integer to day name
	 */
	convertIntToDayName(dayNumber) {
		return this.dayMap.intToName[dayNumber] || "";
	},

	/**
	 * English day names mapping
	 */
	englishDayNames: {
		"Sunday": { short: "Sun", zh: "周日" },
		"Monday": { short: "Mon", zh: "周一" },
		"Tuesday": { short: "Tue", zh: "周二" },
		"Wednesday": { short: "Wed", zh: "周三" },
		"Thursday": { short: "Thu", zh: "周四" },
		"Friday": { short: "Fri", zh: "周五" },
		"Saturday": { short: "Sat", zh: "周六" }
	},

	/**
	 * Format day name based on locale
	 */
	formatDayName(dayName, locale) {
		if (!dayName) return '';
		const dayInfo = this.englishDayNames[dayName];
		if (!dayInfo) return dayName;
		if (locale === 'zh') {
			return dayInfo.zh;
		} else {
			return dayInfo.short;
		}
	},

	/**
	 * Validate hour value (0-23)
	 */
	validateHour(hour) {
		if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
			throw new Error(`Invalid hour: ${hour}. Must be integer 0-23`);
		}
		return true;
	},

	/**
	 * Get day of week (0=Sunday, 6=Saturday)
	 */
	getDayOfWeek(dateStr) {
		if (!dateStr) return null;
		let date = new Date(dateStr);
		if (isNaN(date.getTime())) return null;
		return date.getDay();
	},

	/**
	 * Check if a date matches blocked days
	 */
	isDateBlocked(dateStr, blockedDays) {
		const dayOfWeek = this.getDayOfWeek(dateStr);
		return blockedDays.includes(dayOfWeek.toString());
	},

	/**
	 * Format booking date for API submission
	 * Simply returns YYYY-MM-DD format - NO timezone conversion
	 * Backend will handle timezone interpretation
	 */
	formatBookingDate(dateObj) {
		if (!dateObj) return '';
		// Just format as YYYY-MM-DD, let backend handle timezone
		return this.formatDate(dateObj);
	},

	/**
	 * Format query start date - returns YYYY-MM-DD as-is
	 * NO timezone conversion - backend handles it
	 */
	formatQueryStartDate(dateStr) {
		if (!dateStr) return '';
		// Return date string as-is, backend will interpret
		return this.formatDate(dateStr);
	},

	/**
	 * Format query end date - returns YYYY-MM-DD as-is
	 * NO timezone conversion - backend handles it
	 */
	formatQueryEndDate(dateStr) {
		if (!dateStr) return '';
		// Return date string as-is, backend will interpret
		return this.formatDate(dateStr);
	}
};

export default MTime;
